import { AlertType, ContactPoint } from "../../../types/travelerProfile";
import { SaveProfileError, TravelerProfileContext } from "../TravelerProfileFormHandler";
import { Status } from "../../../service/smsOptInStateService";
import React, { useContext, useEffect } from "react";
import { IntlPhoneInputValue } from "../../../components/cirium/InternationalPhoneInput";
import _ from "lodash";
import { checkSearchResponseAndUpdate } from "../OptInState";
import { Box } from "@flightstats/airframe-react";
import { InternationalPhoneInputWithinFormField } from "../../../components/cirium/InternationalPhoneInputWithinFormField";
import InternationalPhoneInputWithinForm from "../../../components/cirium/InternationalPhoneInputWithinForm";

type ContactPointsPhoneFieldProps = {
    disabled: boolean;
    id: string;
    alertType: AlertType;
    countryCode?: string;
    phoneNumber: string;
    updatePhoneNumberInput: (value: string) => void;
    updateCountryCodeInput: (value: string) => void;
    supportedSMSCountryCodes: string[];
    phoneValidationErrorText: string;
    setPhoneValidationErrorText: (text: string) => void;
    saveProfilePhoneError?: SaveProfileError;
    setSaveProfilePhoneError: (error: SaveProfileError) => void;
    setOptInState?: (status: Status) => void;
    updateContactPoint: (contactPoint: Partial<ContactPoint>) => void;
};

export const preferredCountryCode = 'US';
export const preferredPhoneInput = '+1';

export const ContactPointsPhoneField: React.FC<ContactPointsPhoneFieldProps> = ({
    disabled,
    id,
    alertType,
    countryCode,
    phoneNumber,
    updatePhoneNumberInput,
    updateCountryCodeInput,
    supportedSMSCountryCodes,
    phoneValidationErrorText,
    setPhoneValidationErrorText,
    saveProfilePhoneError,
    setSaveProfilePhoneError,
    setOptInState,
    updateContactPoint,
}) => {
    const { doValidatePhoneAndSearchOptInStatus, addToast } = useContext(
        TravelerProfileContext,
    );
    const countryCodes =
        supportedSMSCountryCodes.length > 0 &&
            countryCode &&
            !supportedSMSCountryCodes.includes(countryCode)
            ? _.union(supportedSMSCountryCodes, [countryCode])
            : supportedSMSCountryCodes;

    const countryCodeDefault = countryCode
        ? countryCode
        : countryCodes.includes(preferredCountryCode)
            ? preferredCountryCode
            : countryCodes[0];
    const getFieldName = () => `${alertType}PhoneInternational`;

    const clearSaveProfilePhoneError = () => {
        const newSaveProfilePhoneError = saveProfilePhoneError || {};
        //this can't know whether it's removing errors this useEffect set or not
        delete newSaveProfilePhoneError[getFieldName()];
        setSaveProfilePhoneError(newSaveProfilePhoneError);
    };

    useEffect(() => {
        //confirm optional prop...
        if (phoneValidationErrorText) {
            const newSaveProfilePhoneError = {
                ...(saveProfilePhoneError || {}),
                //this does have the side effect of replacing any existing error for this input w/ this new error text
                [getFieldName()]: phoneValidationErrorText,
            };
            setSaveProfilePhoneError(newSaveProfilePhoneError);
        } else {
            clearSaveProfilePhoneError();
        }
    }, [phoneValidationErrorText]);

    const intlPhoneOnBlur = (
        event: React.FocusEvent<HTMLInputElement>,
        value: IntlPhoneInputValue,
    ) => {
        const phone = `+${event.target.value.replace(/[^0-9]+/g, '')}`;

        updateContactPoint({
            phoneNumberE164: phone,
            countryCode: value.countryCode,
        });

        if (phone.length > 2) {
            if (doValidatePhoneAndSearchOptInStatus) {
                doValidatePhoneAndSearchOptInStatus(phone, value.countryCode)
                    .then(validateAndSearchResponse => {
                        if (validateAndSearchResponse) {
                            const { search, validate } = validateAndSearchResponse;

                            if (validate && validate.isValid) {
                                setPhoneValidationErrorText('');
                            } else {
                                setPhoneValidationErrorText('Please enter a valid phone number.');
                            }
                            if (setOptInState && addToast) {
                                checkSearchResponseAndUpdate(setOptInState, addToast, search);
                            }
                        } else {
                            addToast && addToast('Error: Validation response missing.', {
                                variant: 'error',
                            });
                        }
                    })
                    .catch(err => {
                        const message =
                            err?.failure?.description || 'Error validating phone number';
                        addToast && addToast(message, { variant: 'error' });
                        console.error(err);
                    });
            }
        }
    };

    const validateInternationalPhone = (value: IntlPhoneInputValue) => {
        let errorMessage;
        if (
            value &&
            value.countryCode &&
            supportedSMSCountryCodes &&
            supportedSMSCountryCodes.length > 0 &&
            !supportedSMSCountryCodes.includes(value.countryCode.toUpperCase())
        ) {
            errorMessage =
                'SMS Messaging to this country is not available for this account. Please contact your travel provider for more information.';
            setPhoneValidationErrorText(errorMessage);
            return errorMessage;
        } else if (
            value &&
            (_.isEmpty(value.number) || value.number === `+${value.dialCode}`)
        ) {
            errorMessage = 'Enter a phone number';
            setPhoneValidationErrorText(errorMessage);
            return errorMessage;
        } else {
            setPhoneValidationErrorText('');
            clearSaveProfilePhoneError();
            return true;
        }
    };

    return (
        <Box className='contact-points-phone-field-box'>
            <InternationalPhoneInputWithinFormField
                validate={validateInternationalPhone}
                isAgent={true}
            >
                <InternationalPhoneInputWithinForm
                    id={`${id}International`}
                    label="Phone Number"
                    disabled={disabled}
                    onlyCountries={countryCodes}
                    countryCode={countryCodeDefault}
                    phoneNumber={phoneNumber}
                    helperText={
                        phoneValidationErrorText ||
                        (saveProfilePhoneError &&
                            saveProfilePhoneError[`${id}International`])
                    }
                    value={{
                        countryCode: countryCode || countryCodeDefault,
                        number: phoneNumber,
                    }}
                    onChange={(val: IntlPhoneInputValue) => {
                        updatePhoneNumberInput(val.number);
                        updateCountryCodeInput(val.countryCode);
                    }}
                    onBlurHandler={intlPhoneOnBlur}
                />
            </InternationalPhoneInputWithinFormField>
        </Box>
    );
};